import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Select, { createFilter } from 'react-select';
import { useMuiError, useTranslation } from 'hooks';
import * as components from './components';
import { baseStyle } from './style';
import { defaultMuiProps } from '../config';

function getValue(indexedOptions, initialValue) {
  if (initialValue) {
    return Array.isArray(initialValue)
      ? initialValue.map(value => indexedOptions[value])
      : indexedOptions[initialValue];
  }
  return initialValue;
}

const defaultGetOptionLabel = option => option.value;
const defaultGetOptionValue = option => option.key;

const baseFilter = createFilter();

function SelectField({
  className,
  classNamePrefix = 'select',
  controlShouldRenderValue,
  disabled = false,
  error,
  filterOption,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  hideSelectedOptions = true,
  isMulti = false,
  maxSelections,
  options = [],
  value,
  onChange,
  ...rest
}) {
  const { t } = useTranslation();
  const [openedMenu, setOpenedMenu] = useState(false);

  const customFilterOption = useCallback(
    (...args) => {
      let result = baseFilter(...args);
      if (filterOption) {
        result = result && filterOption(...args);
      }
      return result;
    },
    [filterOption]
  );

  const handleChange = useCallback(
    values => {
      if (onChange) {
        onChange(
          isMulti
            ? (values || []).map(value => getOptionValue(value))
            : values && getOptionValue(values)
        );
      }
    },
    [getOptionValue, isMulti, onChange]
  );

  const handleMenuClose = useCallback(() => setOpenedMenu(false), []);
  const handleMenuOpen = useCallback(() => setOpenedMenu(true), []);

  const closeOnScroll = useMemo(
    () =>
      openedMenu
        ? event => !event.target?.classList?.contains(`${classNamePrefix}__menu-list`)
        : () => false,
    [classNamePrefix, openedMenu]
  );

  const indexedOptions = useMemo(
    () =>
      options.reduce((acc, option) => {
        acc[getOptionValue(option)] = option;
        return acc;
      }, {}),
    [getOptionValue, options]
  );

  const parsedValue = useMemo(() => getValue(indexedOptions, value), [indexedOptions, value]);

  const optionsProps = useMemo(() => {
    if (isMulti && value?.length >= maxSelections) {
      return {
        noOptionsMessage: () => t('message.maxSelections'),
        options: []
      };
    }
    return {
      noOptionsMessage: () => t('message.noOptions'),
      options
    };
  }, [isMulti, maxSelections, options, t, value]);

  return (
    <Select
      className={className}
      classNamePrefix={classNamePrefix}
      closeMenuOnScroll={closeOnScroll}
      components={components}
      controlShouldRenderValue={controlShouldRenderValue}
      isSearchable={options.length > 10}
      filterOption={customFilterOption}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      hideSelectedOptions={hideSelectedOptions}
      isDisabled={disabled}
      isMulti={isMulti}
      menuPlacement="auto"
      menuPosition="fixed"
      placeholder=""
      styles={baseStyle}
      TextFieldProps={{
        ...defaultMuiProps,
        fullWidth: true,
        margin: 'normal',
        InputLabelProps: {
          shrink: true
        },
        ...useMuiError(error),
        ...rest
      }}
      value={parsedValue || ''}
      onChange={handleChange}
      onMenuClose={handleMenuClose}
      onMenuOpen={handleMenuOpen}
      {...optionsProps}
      {...rest}
    />
  );
}

SelectField.propTypes = {
  className: PropTypes.string,
  classNamePrefix: PropTypes.string,
  controlShouldRenderValue: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  filterOption: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  hideSelectedOptions: PropTypes.bool,
  isMulti: PropTypes.bool,
  maxSelections: PropTypes.number,
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func
};

export default React.memo(SelectField);
